import { CurrencyBtc, Question, Lightbulb, Gear, ArrowRight, Sparkle, Wallet, Cube, Globe, Lock, Users, LinkSimple, TrendUp, Key } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

function Section({ icon: Icon, title, subtitle, tone, children, testid }) {
  const tones = {
    amber: "border-amber-500/30 bg-amber-500/5",
    emerald: "border-emerald-500/25 bg-emerald-500/5",
    blue: "border-blue-500/25 bg-blue-500/5",
  };
  const iconTones = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  };
  return (
    <section className={`rounded-sm border ${tones[tone]} p-5 sm:p-6`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 ${iconTones[tone]}`}>
          <Icon size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-zinc-50">{title}</h2>
          {subtitle && <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">{children}</div>
    </section>
  );
}

function Pill({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
      {Icon && <Icon size={14} weight="bold" className="text-amber-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs font-medium text-zinc-100 mb-0.5">{title}</p>
        <p className="text-[11px] text-zinc-400 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

export default function WhatIsCrypto() {
  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1100px] overflow-x-hidden" data-testid="what-is-crypto-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/30 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-2">
          <CurrencyBtc size={22} weight="bold" className="text-amber-400" />
          <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-500/15 text-amber-300">FOUNDATION</Badge>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">What is Crypto?</h1>
        <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl">
          Before chasing coins, reading charts, or picking a wallet — let's answer the basics.
          This page breaks it down into <span className="text-amber-300 font-medium">What</span>,
          {" "}<span className="text-amber-300 font-medium">Why</span>, and
          {" "}<span className="text-amber-300 font-medium">How</span> — in plain English.
        </p>
      </div>

      {/* WHAT */}
      <Section
        icon={Question}
        title="What is crypto?"
        subtitle="The simplest definition first"
        tone="amber"
        testid="crypto-what"
      >
        <p>
          Crypto (short for <span className="text-amber-300 font-medium">cryptocurrency</span>) is
          {" "}<span className="text-amber-300 font-medium">digital money that lives on the internet</span>, secured
          by math instead of a bank. Nobody owns it. Nobody can shut it off. Anyone with a phone and internet can use it.
        </p>
        <p>
          Every transaction is recorded on a shared, public ledger called a <span className="text-amber-300 font-medium">blockchain</span> — 
          imagine a giant spreadsheet that thousands of computers keep a copy of, all checking each other constantly.
          If one person tries to cheat, the others catch it.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          <Pill
            icon={Globe}
            title="Digital & Global"
            body="Send value anywhere in the world, any time, without a bank or border."
          />
          <Pill
            icon={Users}
            title="Decentralized"
            body="Run by a network of computers, not a single company or government."
          />
          <Pill
            icon={Lock}
            title="Secured by Math"
            body="Cryptography (hence 'crypto') protects balances and transactions — not trust in a company."
          />
        </div>

        <div className="mt-4 p-3 rounded-sm border border-zinc-800 bg-zinc-950/60">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">Quick Example</p>
          <p className="text-xs text-zinc-300">
            <strong className="text-zinc-100">Bitcoin (BTC)</strong> is the original crypto — a fixed supply of 21 million coins, impossible to duplicate.
            <strong className="text-zinc-100"> Ethereum (ETH)</strong> took it further — it lets anyone build apps that run on the blockchain.
            Stablecoins like <strong className="text-zinc-100">USDC</strong> are crypto dollars that stay at $1. All three live on the same rails.
          </p>
        </div>
      </Section>

      {/* WHY */}
      <Section
        icon={Lightbulb}
        title="Why does crypto exist?"
        subtitle="The problems it's trying to solve"
        tone="emerald"
        testid="crypto-why"
      >
        <p>
          The traditional financial system works — for most people, most of the time — but it has real limits.
          Crypto was invented to fix a specific set of them.
        </p>

        <ol className="space-y-2 pl-1">
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">01</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Nobody can freeze or block you.</strong> Your crypto wallet can't be shut down by a bank, a government, or a payment processor. It's yours, controlled by keys only you hold.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">02</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It moves at internet speed.</strong> Sending $10,000 to another country traditionally takes 3–5 business days and $50+ in fees. On-chain it takes minutes and costs cents.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">03</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Supply is rules-based, not political.</strong> Bitcoin will never exceed 21M coins. Dollars can be printed whenever a central bank decides. Crypto removes that discretion.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">04</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It's open to everyone.</strong> About 1.4 billion adults have no access to a bank. A phone + internet is all you need to use crypto — no ID check, no minimum balance, no gatekeeper.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">05</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It's programmable.</strong> Crypto isn't just money — it's money with code attached. Lending, saving, trading, insurance can all be built as apps (DeFi) with no bank in the middle.</span>
          </li>
        </ol>

        <div className="mt-4 p-3 rounded-sm border border-emerald-500/20 bg-emerald-500/5">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <Sparkle size={12} weight="fill" className="inline text-emerald-400 mr-1" />
            <strong className="text-emerald-300">The big takeaway:</strong> crypto isn't trying to replace every dollar you spend.
            It's trying to give people an <em>option</em> — a financial system that's open, permissionless, and
            built on math instead of institutions.
          </p>
        </div>
      </Section>

      {/* HOW */}
      <Section
        icon={Gear}
        title="How does crypto actually work?"
        subtitle="The 4 pieces that make everything click"
        tone="blue"
        testid="crypto-how"
      >
        <p>There are really only <span className="text-blue-300 font-medium">four building blocks</span> you need to understand.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">1</span>
              <Cube size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Blockchain</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">A shared record of every transaction, grouped into "blocks" and chained together in order. Once written, it can't be erased or edited.</p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">2</span>
              <Wallet size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Wallet</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">An app that holds your keys — not your coins. The coins live on the blockchain; your wallet proves they're yours and lets you send them.</p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">3</span>
              <Key size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Keys &amp; Addresses</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Your <em>public address</em> is like an email — share it to receive crypto. Your <em>private key</em> (or seed phrase) is like your password — never share it, ever.</p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">4</span>
              <LinkSimple size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Transactions</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Your wallet signs a transfer with your private key. The network verifies it, adds it to the next block, and updates every copy of the ledger — usually in seconds.</p>
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm border border-blue-500/20 bg-blue-500/5">
          <div className="flex items-center gap-2 mb-2">
            <TrendUp size={14} weight="bold" className="text-blue-400" />
            <p className="text-xs font-medium text-blue-200">Why does crypto have a price?</p>
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">
            Same reason anything has a price: <strong className="text-zinc-100">supply &amp; demand</strong>. More buyers than sellers = price up.
            More sellers than buyers = price down. Crypto prices move faster than stocks because the market runs 24/7 globally, and
            most coins have a smaller total value than big companies — so a few large trades can move the price a lot.
          </p>
        </div>

        <div className="mt-3 p-4 rounded-sm border border-zinc-800 bg-zinc-950/60">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">The One-Sentence Summary</p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="text-amber-300 font-medium">Crypto is internet-native money whose ownership is proven by keys you hold, whose ledger is public, and whose rules are code.</span>
            {" "}Learn the keys, learn the ledger, and the rest is details.
          </p>
        </div>
      </Section>

      {/* Next steps CTA */}
      <div className="rounded-sm border border-amber-500/25 bg-zinc-900/40 p-5 sm:p-6" data-testid="crypto-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight size={16} weight="bold" className="text-amber-400" />
          <h3 className="font-heading text-base font-bold text-zinc-100">You've got the basics — what next?</h3>
        </div>
        <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed mb-4">
          Jump into the rest of the Academy. Each section stacks on the concepts above.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Pill icon={Cube} title="Crypto Academy" body="Smart contracts, how to buy/sell, tokenomics, liquidity pairs." />
          <Pill icon={Lightbulb} title="Why Crypto / DeFi" body="The deeper 'why' — what problems it solves &amp; who it helps." />
          <Pill icon={Globe} title="Blockchain" body="How blockchains actually work under the hood — for the curious." />
        </div>
      </div>
    </div>
  );
}
